import { CommunityMemberRole, CommunityMemberTag } from '@api/ChallengeClient';
import { userSearchDebounce } from '@components/hooks/use-search';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { debounce } from 'lodash';
import React from 'react';
import { SortingRule } from '@components/table';

type VerifiedFilter = '' | 'true' | 'false';

export function useCommunityMembersStore(communityId: number) {
    const { challengeClient } = injectTSDI(Api);

    const [memberRoleFilter, setMemberRoleFilter] = React.useState<
        CommunityMemberRole | ''
    >('');
    const [verifiedFilter, setVerifiedFilter] =
        React.useState<VerifiedFilter>('');

    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: '-createdDate'
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    const communityMembers = useWrapRequest(
        async (
            searchParams?: QueryParamsDto,
            memberRole?: CommunityMemberRole | '',
            verified?: VerifiedFilter
        ) => {
            const query = {
                communityId,
                page: searchParams?.page || 0,
                size: searchParams?.size || 30,
                search: searchParams?.search,
                sort: searchParams?.sort || '-createdDate',
                ...(memberRole ? { memberRole } : {}),
                ...(verified ? { verified: verified === 'true' } : {})
            };

            const result =
                await challengeClient.support.communityMemberSupportControllerGetCommunityMembers(
                    query
                );

            return {
                ...result.data,
                content: result.data.content || []
            };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 30,
                totalPages: 0,
                totalElements: 0
            }
        }
    );

    const debouncedAPICall = React.useCallback(
        debounce(
            (
                searchParams?: QueryParamsDto,
                memberRole?: CommunityMemberRole | '',
                verified?: VerifiedFilter
            ) => {
                communityMembers.request([searchParams, memberRole, verified]);
            },
            100
        ),
        [communityMembers]
    );

    React.useEffect(() => {
        debouncedAPICall(searchParams, memberRoleFilter, verifiedFilter);
    }, [searchParams, memberRoleFilter, verifiedFilter, debouncedAPICall]);

    const handleMemberRoleFilterChange = React.useCallback(
        (value: CommunityMemberRole | '') => {
            setMemberRoleFilter(value);
            dispatch({ type: 'page', page: 0 });
        },
        []
    );

    const handleVerifiedFilterChange = React.useCallback(
        (value: VerifiedFilter) => {
            setVerifiedFilter(value);
            dispatch({ type: 'page', page: 0 });
        },
        []
    );

    const updateMemberTags = React.useCallback(
        async (memberId: number, tags: CommunityMemberTag[]) => {
            await challengeClient.support.communityMemberSupportControllerUpdateCommunityMemberTags(
                memberId,
                { tags }
            );
            communityMembers.request(
                [searchParams, memberRoleFilter, verifiedFilter],
                { stateLoading: false }
            );
        },
        [
            challengeClient,
            communityMembers,
            searchParams,
            memberRoleFilter,
            verifiedFilter
        ]
    );

    const updateMemberJoinDay = React.useCallback(
        async (memberId: number, joinDay: string) => {
            await challengeClient.support.communityMemberSupportControllerUpdateCommunityMemberJoinDay(
                memberId,
                { joinDay }
            );
            communityMembers.request(
                [searchParams, memberRoleFilter, verifiedFilter],
                { stateLoading: false }
            );
        },
        [
            challengeClient,
            communityMembers,
            searchParams,
            memberRoleFilter,
            verifiedFilter
        ]
    );

    const updateMemberRole = React.useCallback(
        async (memberId: number, role: CommunityMemberRole) => {
            await challengeClient.support.communityMemberSupportControllerUpdateCommunityMemberRole(
                memberId,
                { role }
            );
            communityMembers.request(
                [searchParams, memberRoleFilter, verifiedFilter],
                { stateLoading: false }
            );
        },
        [
            challengeClient,
            communityMembers,
            searchParams,
            memberRoleFilter,
            verifiedFilter
        ]
    );

    return {
        communityMembers,
        searchParams,
        memberRoleFilter,
        verifiedFilter,
        sorting,
        setSorting,
        setMemberRoleFilter: handleMemberRoleFilterChange,
        setVerifiedFilter: handleVerifiedFilterChange,
        setSearchTerm,
        updateMemberTags,
        updateMemberJoinDay,
        updateMemberRole,
        dispatch
    };
}
